import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DAppClient, NetworkType } from "@airgap/beacon-sdk";
import { TezosToolkit } from "@taquito/taquito";
import { BeaconWallet } from "@taquito/beacon-wallet";
import CustomToastContainer from "../../Common/CustomToastContainer";
import CryptoJS from "crypto-js";
import Clock from "../components/Clock";
import { createHistory } from "../../Common/createHistory";
import { io } from "socket.io-client";

// Create a single socket connection
const socket = io(process.env.REACT_APP_SOCKET_SERVER_URL, {
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
});
const CATEGORY_OPTIONS = ["All", "Image", "Video", "Gif", "Audio"];
const SALE_OPTIONS = ["All", "Buy Now", "Has Offer", "Auction"];
const ITEM_OPTIONS = ["All Items", "Single Item", "Collection"];
const domainpath = process.env.REACT_APP_API_DOMAIN_ENDPOINT;
const generateAvatarurl = process.env.REACT_APP_GENERATE_AVATAR_URL;
const tezos = new TezosToolkit(process.env.REACT_APP_RPC_URL); // or use a custom RPC endpoint

const wallet = new BeaconWallet({
  name: "ArtCollect",
  preferredNetwork: NetworkType.GHOSTNET, // or GHOSTNET
});

tezos.setWalletProvider(wallet);
const Explore = () => {
  const dropdownRef = useRef(null);

  const { category: urlCategory } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCategoryFilter(false);
        setShowSaleFilter(false);
        setShowItemFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  /* ------------------------------------------------------------------
   * Local state
   * ---------------------------------------------------------------- */
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  // Filter state
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(
    CATEGORY_OPTIONS.includes(
      urlCategory?.charAt(0).toUpperCase() + urlCategory?.slice(1).toLowerCase()
    )
      ? urlCategory.charAt(0).toUpperCase() + urlCategory.slice(1).toLowerCase()
      : "All"
  );
  const [saleType, setSaleType] = useState("All");
  const [itemType, setItemType] = useState("All Items");

  // Filter dropdown visibility
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [showSaleFilter, setShowSaleFilter] = useState(false);
  const [showItemFilter, setShowItemFilter] = useState(false);

  /* ------------------------------------------------------------------
   * Fetch marketplace stats on mount
   * ---------------------------------------------------------------- */
  useEffect(() => {
    const fetchData = async () => {
      const storedAddress = localStorage.getItem("walletAddress");
      try {
        const res = await fetch(
          `${domainpath}/api/stats?userId=${storedAddress}&limit=12&offset=${offset}`
        );
        const json = await res.json();
        setRawData((prev) => [...prev, ...json.nftList]);
        if (json.nftList.length < 12) setHasMore(false);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [offset]);

  /* ------------------------------------------------------------------
   * Handle URL category parameter changes
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (urlCategory) {
      const formattedCategory =
        urlCategory.charAt(0).toUpperCase() +
        urlCategory.slice(1).toLowerCase();
      if (CATEGORY_OPTIONS.includes(formattedCategory)) {
        setCategory(formattedCategory);
      } else {
        navigate("/explore", { replace: true });
      }
    } else {
      setCategory("All");
    }
  }, [urlCategory, navigate]);

  /* ------------------------------------------------------------------
   * Handle category filter changes (update URL)
   * ---------------------------------------------------------------- */
  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    if (newCategory === "All") {
      navigate(`/explore/${newCategory.toLowerCase()}`);
    } else {
      navigate(`/explore/${newCategory.toLowerCase()}`);
    }
  };

  /* ------------------------------------------------------------------
   * Derived & memoised helpers
   * ---------------------------------------------------------------- */
  const preparedItems = useMemo(() => {
    if (!rawData) return [];

    // Normalize NFT items
    const nftCards =
      rawData.nftList?.map((nft) => ({
        ...nft,
        __type: "single",
        src: resolveMediaUrl(nft),
      })) ?? [];

    // Bundle/collection cards
    const bundleCards =
      rawData.bundleList?.map((bundle) => ({
        ...bundle,
        __type: "collection",
        title: bundle.value?.description ?? `Bundle ${bundle.key}`,
        src: ipfsToHttp(bundle.value?.ipfs_url),
      })) ?? [];

    return [...nftCards, ...bundleCards];
  }, [rawData]);

  /* ------------------------------------------------------------------
   * Filter logic
   * ---------------------------------------------------------------- */
  const filteredItems = useMemo(() => {
    return preparedItems.filter((item) => {
      // Search
      if (search && !item.title?.toLowerCase().includes(search.toLowerCase()))
        return false;

      // Category
      if (category !== "All") {
        const mediaType = detectCategory(item);
        if (mediaType !== category) return false;
      }

      // Sale Type
      if (saleType !== "All") {
        const matchesSale = {
          "Buy Now": item.blockchain_metadata?.open_to_sell,
          "Has Offer": item.blockchain_metadata?.open_to_offers,
          Auction:
            item.blockchain_metadata?.open_to_auction || item.isAuctionActive,
        }[saleType];
        if (!matchesSale) return false;
      }

      // Item Type
      if (itemType === "Single Item" && item.__type !== "single") return false;
      if (itemType === "Collection" && item.__type !== "collection")
        return false;

      return true;
    });
  }, [search, category, saleType, itemType, preparedItems]);

  /* ------------------------------------------------------------------
   * Filter utilities
   * ---------------------------------------------------------------- */
  const handleClearFilters = () => {
    setSearch("");
    setCategory("All");
    setSaleType("All");
    setItemType("All Items");
    navigate("/explore/all");
  };

  const getFilterCount = () => {
    let count = 0;
    if (search) count++;
    if (category !== "All") count++;
    if (saleType !== "All") count++;
    if (itemType !== "All Items") count++;
    return count;
  };

  const filterCount = getFilterCount();

  /* ------------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------- */
  if (loading) {
    return (
      <section className="jumbotron breadcumb no-bg">
        <div className="mainbreadcumb container">
          <div className="RoadmapSpinnerDiv" id="RoadmapSpinnerDiv">
            <span className="RoadmapSpinner"></span>
          </div>
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <div className="mx-auto max-w-lg py-20 text-center">
        <p className="mb-4 text-xl font-semibold text-red-600">
          Something went wrong
        </p>
        <p className="text-sm text-gray-500">{error}</p>
      </div>
    );
  }

  return (
    <section className="jumbotron breadcumb no-bg">
      <div className="mainbreadcumb container">
        {/* FILTER BAR */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
          {/* Search */}
          <div className="relative flex-1">
            <input
              type="search"
              placeholder="Search items…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white/70 px-4 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none appearance-none [appearance:textfield] [&::-webkit-search-cancel-button]:appearance-none"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>

          {/* Filter dropdowns */}
          <div className="flex flex-wrap items-center gap-2" ref={dropdownRef}>
            <FilterDropdown
              label="Category"
              options={CATEGORY_OPTIONS}
              value={category}
              onChange={handleCategoryChange}
              isOpen={showCategoryFilter}
              toggleOpen={() => setShowCategoryFilter(!showCategoryFilter)}
              onClose={() => setShowCategoryFilter(false)}
            />

            <FilterDropdown
              label="Sale Type"
              options={SALE_OPTIONS}
              value={saleType}
              onChange={setSaleType}
              isOpen={showSaleFilter}
              toggleOpen={() => setShowSaleFilter(!showSaleFilter)}
              onClose={() => setShowSaleFilter(false)}
            />

            <FilterDropdown
              label="Item"
              options={ITEM_OPTIONS}
              value={itemType}
              onChange={setItemType}
              isOpen={showItemFilter}
              toggleOpen={() => setShowItemFilter(!showItemFilter)}
              onClose={() => setShowItemFilter(false)}
            />

            {/* Clear button */}
            {filterCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="ml-2 rounded-lg border border-gray-300 bg-white/70 px-3 py-2 text-sm text-gray-600 shadow-sm hover:bg-gray-50"
              >
                Clear ({filterCount})
              </button>
            )}
          </div>
        </div>

        {/* CARD GRID */}
        {filteredItems.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-gray-500">
              No items match your filters.
            </p>
            {filterCount > 0 && (
              <button
                onClick={handleClearFilters}
                className="mt-2 text-sm text-indigo-600 hover:text-indigo-800"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4">
            {filteredItems.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------------
 * Enhanced Filter Dropdown Component
 * --------------------------------------------------------------------- */
const FilterDropdown = ({
  label,
  options,
  value,
  onChange,
  isOpen,
  toggleOpen,
  onClose,
}) => {
  return (
    <div className="relative">
      <button
        onClick={toggleOpen}
        className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
          value !== "All" && value !== "All Items"
            ? "border-indigo-500 bg-indigo-50 text-indigo-700"
            : "border-gray-300 bg-white/70 text-gray-600"
        }`}
        style={{ background: "#faf6f1", color: "#000" }}
      >
        <span>{label}</span>
        <span className="text-xs">{isOpen ? "▲" : "▼"}</span>
      </button>

      {isOpen && (
        <div
          className="absolute right-0 z-10 mt-1 w-40   shadow-lg"
          style={{
            background: "#faf6f1",
            color: "#000",
            border: "1px solid #888",
          }}
        >
          <div className="p-2">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => {
                  onChange(option);
                  onClose();
                }}
                className={`block w-full px-3 py-1.5 text-left text-sm rounded mt-1 ${
                  value === option
                    ? "bg-black text-white"
                    : "hover:bg-black hover:text-white"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------
 * Item Card Component
 * --------------------------------------------------------------------- */
const ItemCard = ({ item }) => {
  const toastRef = useRef();
  const [loading, setloading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [render, setRender] = useState(0);
  const [userAddress, setUserAddress] = useState();
  const [showBidModal, setShowBidModal] = useState(false);
  const [showMakeModal, setshowMakeModal] = useState(false);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidError, setBidError] = useState(false);
  useEffect(() => {
    const storedAddress = localStorage.getItem("walletAddress");
    if (storedAddress) {
      setUserAddress(storedAddress);
    }
  }, []);
  const handlePlaceBid = async () => {
    setloading(true);

    // Validate bid amount
    if (!bidAmount || isNaN(bidAmount) || bidAmount <= 0) {
      toastRef.current.addToast("Please enter a valid bid amount.", "warning");
      setloading(false);
      return;
    }

    try {
      // Request wallet permissions
      await wallet.requestPermissions();
      const userAddress = await wallet.getPKH();

      if (!userAddress) {
        toastRef.current.addToast("Wallet connection failed.", "error");
        setloading(false);
        return;
      }
      const storedAddress = localStorage.getItem("walletAddress");
      if (!storedAddress || storedAddress !== userAddress) {
        localStorage.setItem("walletAddress", userAddress);
      }
      // Get contract and decrypt NFT ID
      const contract = await tezos.wallet.at(
        process.env.REACT_APP_CONTRACT_ADDRESS
      );
      const nft_id = decrypt(selectedNFT.nft_id);

      // Convert to mutez (1 ꜩ = 1,000,000 mutez)
      const mutezAmount = Math.floor(Number(bidAmount) * 1_000_000);

      // Call the bidactn entry point
      const op = await contract.methods
        .bidactn(nft_id)
        .send({ amount: bidAmount });

      toastRef.current.addToast(
        "Bid placed! Waiting for confirmation...",
        "info"
      );

      await op.confirmation(1); // wait for 1 block confirmation
      socket.emit("new-bid", {
        ownerWallet: selectedNFT.blockchain_metadata.owner,
        bidderWallet: userAddress,
        amount: bidAmount,
        itemName: selectedNFT.blockchain_metadata.name,
        itemId: selectedNFT._id,
      });
      toastRef.current.addToast("Bid placed successfully!", "success");
      setShowBidModal(false);
      setBidAmount("");
      setRender(render + 1); // Refresh data
    } catch (error) {
      console.error("Error placing bid:", error);

      let errorMessage = "Failed to place bid";
      if (error.message.includes("Auction does not exist")) {
        errorMessage = "Auction not found";
      } else if (error.message.includes("Auction has ended")) {
        errorMessage = "Auction has already ended";
      } else if (error.message.includes("Bid must be higher")) {
        errorMessage = "Your bid must be higher than current highest bid";
      } else if (error.message.includes("user rejected")) {
        errorMessage = "Transaction was cancelled";
      }

      toastRef.current.addToast(errorMessage, "error");
    } finally {
      setloading(false);
    }
  };
  const handleBidChange = (e, currentBid) => {
    const value = e.target.value;
    setBidAmount(value);

    const parsedValue = parseFloat(value);
    if (!isNaN(parsedValue) && parsedValue > currentBid) {
      setBidError(false);
    } else {
      setBidError(true);
    }
  };

  const handleMakeOffer = async () => {
    setloading(true);

    const userAddress = localStorage.getItem("walletAddress");

    // 5) Build & Send Transaction
    let op;
    try {
      const contract = await tezos.wallet.at(
        process.env.REACT_APP_CONTRACT_ADDRESS
      );
      const nftId = decrypt(selectedNFT.nft_id);
      const receiverWallet = selectedNFT.blockchain_metadata?.owner;
      const platformFeePercent = parseFloat(
        process.env.REACT_APP_PLATFORM_FEE || "0"
      );
      const platformOwner = process.env.REACT_APP_PLATFORM_OWNER;

      if (!platformOwner || isNaN(platformFeePercent)) {
        throw new Error("Platform fee or owner is not configured properly.");
      }

      // ✅ Calculate platform fee as amount * (feePercent / 100)
      const platformFeeMutez = Math.floor(
        bidAmount * (platformFeePercent / 100)
      );
      // console.log(nftId, userAddress, platformOwner, platformFeeMutez);

      op = await contract.methods
        .makeoffer(nftId, userAddress, platformOwner, platformFeeMutez)
        .send({ amount: bidAmount });
      socket.emit("make-offer", {
        senderWallet: userAddress,
        receiverWallet: receiverWallet,
        nftId: selectedNFT._id,
        nftName: selectedNFT.title || `NFT #${nftId}`,
        offerAmount: bidAmount,
        timestamp: new Date().toISOString(),
      });
      const historyData = {
        action: "offer",
        user: userAddress,
        nft_id: nftId,
        details: `${userAddress} made an offer of ${bidAmount} ꜩ on NFT #${nftId}`,
      };
      await createHistory(historyData);
    } catch (err) {
      console.error("Transaction send error:", err); // Log the full error for debugging
      const msg = err.message || "Failed to send offer transaction.";
      const isAbort =
        err.errorType === "ABORTED_ERROR" ||
        err.name === "UnknownBeaconError" ||
        msg.toLowerCase().includes("user rejected");
      toastRef.current.addToast(
        isAbort ? "Transaction was cancelled by the user." : msg,
        "error"
      );
      setloading(false);
      return;
    }

    // 6) Wait for confirmation
    try {
      await op.confirmation(1);
    } catch (err) {
      console.error("Transaction confirmation error:", err); // Log the full error for debugging
      toastRef.current.addToast(
        err.message || "Transaction confirmation failed.",
        "error"
      );
      setloading(false);
      return;
    }

    // 7) Success!
    toastRef.current.addToast("Offer placed successfully!", "success");
    setshowMakeModal(false);
    setBidAmount("");
    setRender((r) => r + 1);
    setloading(false);
  };

  const handleBuyNow = async (nft) => {
    setloading(true);

    try {
      // 1) Request wallet permissions
      await wallet.requestPermissions();
      const userAddress = await wallet.getPKH();

      if (!userAddress) {
        toastRef.current.addToast("Wallet connection failed.", "error");
        setloading(false);
        return;
      }

      // Store address in localStorage if not already present
      const storedAddress = localStorage.getItem("walletAddress");
      if (!storedAddress || storedAddress !== userAddress) {
        localStorage.setItem("walletAddress", userAddress);
      }
      if (storedAddress === nft.blockchain_metadata.owner) {
        toastRef.current.addToast(
          "You already own this NFT — purchasing it again isn’t allowed.",
          "error"
        );
        window.location.reload();
        return;
      }
      // 2) Get contract instance
      const contract = await tezos.wallet.at(
        process.env.REACT_APP_CONTRACT_ADDRESS
      );

      // 3) Decrypt NFT ID (assuming it's encrypted)
      const nftId = decrypt(nft.nft_id);

      if (!nftId || isNaN(nftId)) {
        toastRef.current.addToast("Invalid NFT ID.", "error");
        setloading(false);
        return;
      }

      // 4) Get platform fee and owner from env
      const platformFeePercent = parseFloat(
        process.env.REACT_APP_PLATFORM_FEE || "2.5" // Default to 2.5% if not set
      );
      const platformOwner = process.env.REACT_APP_PLATFORM_OWNER;

      if (!platformOwner) {
        toastRef.current.addToast("Platform owner not configured.", "error");
        setloading(false);
        return;
      }

      // 5) Calculate amounts (convert from mutez to tez)
      const nftPriceTez = toTez(nft.blockchain_metadata.price);
      const platformFeeTez = nftPriceTez * (platformFeePercent / 100);
      const totalAmountTez = nftPriceTez + platformFeeTez;

      // Convert back to mutez for the contract call
      const platformFeeMutez = Math.floor(platformFeeTez * 1000000);

      // 6) Call the contract with properly formatted parameters
      const op = await contract.methods
        .buynft(
          parseInt(nftId), // Ensure nft_id is a number
          platformOwner, // platform_owner address
          platformFeeMutez // platform_fee in mutez
        )
        .send({
          amount: totalAmountTez,
          mutez: false, // amount is in tez
        });

      toastRef.current.addToast(
        "Transaction sent! Waiting for confirmation...",
        "info"
      );

      // 7) Wait for confirmation
      await op.confirmation(1);
      // ✅ Emit socket event
      socket.emit("successful-purchase", {
        buyerWallet: userAddress,
        sellerWallet: nft.blockchain_metadata.owner,
        price: nftPriceTez,
        itemName: nft.blockchain_metadata.name,
        itemId: nft._id,
      });
      // 8) Create history record
      const historyData = {
        action: "buy",
        user: userAddress,
        nft_id: nftId,
        details: `${userAddress} purchased NFT #${nftId} for ${nftPriceTez} ꜩ`,
      };
      await createHistory(historyData);

      // 9) Success!
      toastRef.current.addToast("Purchase completed successfully!", "success");
      setRender((prev) => prev + 1); // Refresh the UI
    } catch (error) {
      console.error("Buy Now Error:", error);

      let errorMessage = "Failed to complete purchase";
      if (error.message.includes("user rejected")) {
        errorMessage = "Transaction was cancelled";
      } else if (error.message.includes("NFT does not exist")) {
        errorMessage = "NFT no longer exists";
      } else if (error.message.includes("not for sale")) {
        errorMessage = "NFT is no longer for sale";
      } else if (error.message.includes("Insufficient funds")) {
        errorMessage = "Insufficient funds for purchase";
      } else if (error.message.includes("Invalid contract parameter")) {
        errorMessage = "Invalid transaction parameters";
      }

      toastRef.current.addToast(errorMessage, "error");
    } finally {
      setloading(false);
    }
  };
  const SECRET_KEY =
    "0123456789abcdef0123456789abcdef0123456789abcdef0123456789abcdef"; // Same key used for encryption
  const ENCRYPTION_ALGORITHM = CryptoJS.AES; // Using AES for encryption

  // Function to decrypt data
  function decrypt(encryptedText) {
    // Split the encrypted text to get IV and encrypted data
    const [ivHex, encryptedData] = encryptedText.split(":");

    // Convert IV and encrypted data from hex to word arrays
    const iv = CryptoJS.enc.Hex.parse(ivHex);
    const encryptedDataBytes = CryptoJS.enc.Hex.parse(encryptedData);

    // Decrypt the data
    const decryptedData = ENCRYPTION_ALGORITHM.decrypt(
      { ciphertext: encryptedDataBytes },
      CryptoJS.enc.Hex.parse(SECRET_KEY),
      { iv: iv }
    );

    // Convert decrypted bytes to UTF-8 string
    return decryptedData.toString(CryptoJS.enc.Utf8);
  }
  const toggleLike = async (artId, liked, nftid, artOwnerWallet, nftName) => {
    if (!userAddress) {
      try {
        // 1. First check if wallet is connected without forcing permissions
        const activeAccount = await wallet.client.getActiveAccount();

        if (!activeAccount) {
          // 2. Only request permissions if no active account exists
          await wallet.requestPermissions({
            forcePermissionRequest: true, // Ensures modal shows
          });
        }

        // 3. Get address after successful connection
        const address = await wallet.getPKH();
        setUserAddress(address);
        localStorage.setItem("walletAddress", address);

        try {
          const url = `${domainpath}/api/art/${artId}/${
            liked ? "unlike" : "like"
          }`;
          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId: userAddress }), // Send user ID
          });
          const nftId = decrypt(nftid);
          const likerName = `${userAddress.slice(0, 6)}...${userAddress.slice(
            -4
          )}`;

          const historyData = {
            action: liked ? "unlike" : "like",
            user: userAddress,
            details: `${userAddress} ${
              liked ? "unlike" : "like"
            } art with ID ${artId}`,
            nft_id: nftId,
          };
          await createHistory(historyData);
          if (!response.ok) throw new Error("Failed to update like status");

          const updatedArt = await response.json();
          socket.emit(liked ? "unlike-art" : "like-art", {
            likerName,
            likerWallet: userAddress,
            ownerWallet: artOwnerWallet,
            nftId: artId,
            nftName: nftName || `NFT #${nftId}`,
            timestamp: new Date().toISOString(),
          });
          // Update the specific artwork in the state
        } catch (error) {
          //console.error("Error liking/unliking art:", error);
        }
        window.location.reload();
      } catch (error) {
        console.error("Connection error:", error);
        toastRef.current.addToast(
          "Failed to connect wallet. Please try again.",
          "error"
        );
      }
      return;
    } else {
      try {
        const url = `${domainpath}/api/art/${artId}/${
          liked ? "unlike" : "like"
        }`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userId: userAddress }), // Send user ID
        });
        const nftId = decrypt(nftid);
        const likerName = `${userAddress.slice(0, 6)}...${userAddress.slice(
          -4
        )}`;

        const historyData = {
          action: liked ? "unlike" : "like",
          user: userAddress,
          details: `${userAddress} ${
            liked ? "unlike" : "like"
          } art with ID ${artId}`,
          nft_id: nftId,
        };
        await createHistory(historyData);
        if (!response.ok) throw new Error("Failed to update like status");

        const updatedArt = await response.json();
        socket.emit(liked ? "unlike-art" : "like-art", {
          likerName,
          likerWallet: userAddress,
          ownerWallet: artOwnerWallet,
          nftId: artId,
          nftName: nftName || `NFT #${nftId}`,
          timestamp: new Date().toISOString(),
        });
        window.location.reload();
        // Update the specific artwork in the state
      } catch (error) {
        //console.error("Error liking/unliking art:", error);
      }
    }
  };
  const handleFollowToggle = async (ownerAddress) => {
    if (!userAddress) {
      try {
        // 1. First check if wallet is connected without forcing permissions
        const activeAccount = await wallet.client.getActiveAccount();

        if (!activeAccount) {
          // 2. Only request permissions if no active account exists
          await wallet.requestPermissions({
            forcePermissionRequest: true, // Ensures modal shows
          });
        }

        // 3. Get address after successful connection
        const address = await wallet.getPKH();
        setUserAddress(address);
        localStorage.setItem("walletAddress", address);
        const path = isFollowing
          ? `${domainpath}/api/follow/unfollow`
          : `${domainpath}/api/follow/`;
        const response = await fetch(path, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            follower: address,
            following: ownerAddress,
          }),
        });

        if (response.ok) {
          socket.emit("follow-user", {
            followerWallet: address,
            followerName: address,
            followedWallet: ownerAddress,
            timestamp: new Date().toISOString(),
          });
          isFollowing
            ? toastRef.current.addToast(
                "User UnFollowed Successfully",
                "success"
              )
            : toastRef.current.addToast(
                "User Followed Successfully",
                "success"
              );
          setIsFollowing((prev) => !prev);
        }
      } catch (error) {
        console.error("Connection error:", error);
        toastRef.current.addToast(
          "Failed to connect wallet. Please try again.",
          "error"
        );
      }
      return;
    } else {
      const path = isFollowing
        ? `${domainpath}/api/follow/unfollow`
        : `${domainpath}/api/follow/`;
      const response = await fetch(path, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          follower: userAddress,
          following: ownerAddress,
        }),
      });
      if (response.ok) {
        socket.emit("follow-user", {
          followerWallet: userAddress,
          followerName: userAddress,
          followedWallet: ownerAddress,
          timestamp: new Date().toISOString(),
        });
        isFollowing
          ? toastRef.current.addToast("User UnFollowed Successfully", "success")
          : toastRef.current.addToast("User Followed Successfully", "success");
        setIsFollowing((prev) => !prev);
      }
    }
  };
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const openBidModal = async (nft) => {
    if (!userAddress) {
      try {
        // 1. First check if wallet is connected without forcing permissions
        const activeAccount = await wallet.client.getActiveAccount();

        if (!activeAccount) {
          // 2. Only request permissions if no active account exists
          await wallet.requestPermissions({
            forcePermissionRequest: true, // Ensures modal shows
          });
        }

        // 3. Get address after successful connection
        const address = await wallet.getPKH();
        setUserAddress(address);
        localStorage.setItem("walletAddress", address);
        if (nft?.blockchain_metadata.owner === address) {
          toastRef.current.addToast(
            "Oops! You already own this artwork — place a bid isn’t necessary.",
            "error"
          );

          window.location.reload();
        } else {
          setSelectedNFT(nft);
          setShowBidModal(true);
        }
      } catch (error) {
        console.error("Connection error:", error);
        toastRef.current.addToast(
          "Failed to connect wallet. Please try again.",
          "error"
        );
      }
      return;
    } else {
      setSelectedNFT(nft);
      setShowBidModal(true);
    }
  };
  const openMakeofferModal = async (nft) => {
    if (!userAddress) {
      try {
        // 1. First check if wallet is connected without forcing permissions
        const activeAccount = await wallet.client.getActiveAccount();

        if (!activeAccount) {
          // 2. Only request permissions if no active account exists
          await wallet.requestPermissions({
            forcePermissionRequest: true, // Ensures modal shows
          });
        }

        // 3. Get address after successful connection
        const address = await wallet.getPKH();
        setUserAddress(address);
        localStorage.setItem("walletAddress", address);
        if (nft?.blockchain_metadata.owner === address) {
          toastRef.current.addToast(
            "Oops! You already own this artwork — making an offer isn’t necessary.",
            "error"
          );

          window.location.reload();
        } else {
          setSelectedNFT(nft);
          setshowMakeModal(true);
        }
      } catch (error) {
        console.error("Connection error:", error);
        toastRef.current.addToast(
          "Failed to connect wallet. Please try again.",
          "error"
        );
      }
      return;
    } else {
      setSelectedNFT(nft);
      setshowMakeModal(true);
    }
  };

  const navigate = useNavigate();
  const media = detectCategory(item);
  const imageUrl = ipfsToHttp(item.src);

  const generateAvatar = (walletAddress) => {
    return `${generateAvatarurl}/${walletAddress}.png?size=200x200`;
  };

  const handleItemClick = () => {
    if (item.__type === "collection") {
      navigate(`/collection/${item.key}`);
    } else {
      navigate(`/art/${item.id}`);
    }
  };

  const toTez = (mutez) => mutez / 1000000;
  // const price = item.price ? toTez(item.price) : 0;
  const price = item.price;

  const editionNumber = item.blockchain_metadata?.edition;
  const totalSupply = item.blockchain_metadata?.total_supply;
  const isAuctionActive = item.hasAuction && item.auctionDetails;
  const auctionEnded =
    isAuctionActive && new Date(item.auctionDetails.end_time) < new Date();
  const auctionEndTime = isAuctionActive ? item.auctionDetails.end_time : null;
  const isOwner =
    !!userAddress &&
    item.blockchain_metadata?.owner?.toLowerCase() ===
      userAddress.toLowerCase();

  return (
    <>
      <div className="explorecategory ">
        <div className="nft__item m-0 h-full min-h-[490px] max-h-[425px] flex flex-col justify-between">
          {item?.auctionDetails?.end_time &&
          new Date(item.auctionDetails.end_time) > new Date() ? (
            <div className="de_countdown">
              <Clock
                deadline={formatDate(new Date(item.auctionDetails.end_time))}
              />
            </div>
          ) : null}

          <div className="relative author_list_pp">
            <span
              onClick={() =>
                navigate(`/profile/${item.blockchain_metadata?.owner}`)
              }
            >
              <img
                className="lazy"
                src={
                  generateAvatar(item.blockchain_metadata?.owner) ||
                  "./img/author/author-1.jpg"
                }
                alt=""
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = `https://ui-avatars.com/api/?name=${item.blockchain_metadata?.owner?.slice(
                    2,
                    4
                  )}&background=random`;
                }}
              />
              <i className="fa fa-check"></i>
            </span>
          </div>

          <div className="nft__item_first">
            <div
              className={
                media === "Audio"
                  ? "music_nft nft__item_wrap group relative h-72 sm:h-72 md:h-72 lg:h-72 xl:h-72 overflow-hidden"
                  : "nft__item_wrap group relative h-72 sm:h-72 md:h-72 lg:h-72 xl:h-72 overflow-hidden"
              }
            >
              <span className="">
                {imageUrl ? (
                  <>
                    {media === "Image" ? (
                      <img
                        decoding="async"
                        src={imageUrl}
                        className="lazy object-cover h-60 w-full transition-transform duration-300 ease-in-out group-hover:scale-105"
                        alt={item.title}
                        // onError={(e) => {
                        //   e.target.onerror = null;
                        //   e.target.src = "./img/items/dummy.jpg";
                        // }}
                        style={{ width: "100%", height: "auto" }}
                      />
                    ) : media === "Video" ? (
                      <video
                        src={imageUrl}
                        decoding="async"
                        className="lazy object-cover h-60 w-full transition-transform duration-300 ease-in-out group-hover:scale-105"
                        controls
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : media === "Audio" ? (
                      <audio
                        src={imageUrl}
                        decoding="async"
                        className="w-10/12"
                        controls
                        style={{ width: "90%", margin: "0 auto" }}
                      />
                    ) : (
                      <img
                        decoding="async"
                        src="./img/items/dummy.jpg"
                        className="lazy object-cover h-60 w-full transition-transform duration-300 ease-in-out group-hover:scale-105"
                        alt={item.title}
                      />
                    )}
                  </>
                ) : (
                  <p>No media available</p>
                )}
              </span>
            </div>
          </div>

          <div className="nft__item_info">
            <div
              //onClick={() => navigate(`/art/${item.id}`)}
              onClick={handleItemClick}
              className="nft__item_title"
            >
              <h4>{item.title || "Untitled"}</h4>
            </div>

            {editionNumber && totalSupply && (
              <p className="text-sm text-gray-500">
                Edition {editionNumber} of {totalSupply}
              </p>
            )}

            <div className="">
              {isAuctionActive ? (
                <>
                  {item.auctionDetails?.highest_bid > 0 ? (
                    <div className="nft__item_price">
                      <div className="text-gray-600 font-semibold">
                        Current Bid: {toTez(item.auctionDetails.highest_bid)} ꜩ
                      </div>
                    </div>
                  ) : (
                    <div className="nft__item_price">
                      <span className="text-gray-600 font-semibold">
                        Opening Bid: {price} ꜩ
                      </span>
                    </div>
                  )}
                </>
              ) : item.blockchain_metadata?.open_to_offers ? (
                <>
                  {item.totalBidders > 0 ? (
                    <span className="text-gray-600 text-sm font-semibold">
                      Current Offer: {toTez(item.currentBid || 0)} ꜩ
                    </span>
                  ) : (
                    <span className="text-gray-600 text-sm font-semibold">
                      No Offers Yet
                    </span>
                  )}
                  {item.totalBidders > 0 && (
                    <span className="ml-2 text-sm text-gray-500 font-semibold">
                      ({item.totalBidders}{" "}
                      {item.totalBidders === 1 ? "offer" : "offers"})
                    </span>
                  )}
                </>
              ) : (
                <div className="nft__item_price">
                  <span className="text-gray-600 text-sm font-semibold">
                    Price: {price} ꜩ
                  </span>
                </div>
              )}
            </div>

            <div className="h-10 nft__item_action-main w-full">
              {isAuctionActive && !auctionEnded && !isOwner ? (
                <div
                  className={`nft__item_action mt-1 ${
                    isOwner ? "w-full" : "w-1/2"
                  }`}
                >
                  <span
                    onClick={() => openBidModal(item)}
                    className="place-btn"
                  >
                    Place Bid
                  </span>
                </div>
              ) : null}

              {!isAuctionActive &&
              item.blockchain_metadata?.open_to_offers &&
              !isOwner ? (
                <div
                  className={`nft__item_action mt-1 ${
                    isOwner ? "w-full" : "w-1/2"
                  }`}
                >
                  <span
                    onClick={() => openMakeofferModal(item)}
                    className="place-btn"
                  >
                    Make an Offer
                  </span>
                </div>
              ) : null}

              {!isAuctionActive &&
              item.blockchain_metadata?.open_to_sell &&
              !isOwner ? (
                <div
                  className={`nft__item_action mt-1 ${
                    isOwner ? "w-full" : "w-1/2"
                  }`}
                >
                  <span
                    onClick={() => handleBuyNow(item)}
                    className="place-btn"
                  >
                    Buy Now
                  </span>
                </div>
              ) : null}

              {isOwner && !item.blockchain_metadata?.open_to_sell ? (
                <div
                  className={`nft__item_action mt-1 ${
                    isOwner ? "w-full" : "w-1/2"
                  }`}
                  onClick={() =>
                    item.value.open_to_auction
                      ? navigate(`/art/${item.id}?bids`)
                      : navigate(`/art/${item.id}?offers`)
                  }
                >
                  <span className="place-btn">
                    {item.blockchain_metadata?.open_to_auction === true
                      ? "Check Bids"
                      : "Check Offers"}
                  </span>
                </div>
              ) : null}

              <div
                className={`flex justify-end ${
                  isOwner || item.__type === "collection" ? "w-full" : "w-1/2"
                }`}
              >
                <div
                  className="nft__item_like"
                  onClick={(e) =>
                    toggleLike(item.id, item.likedByUser, item.nft_id)
                  }
                >
                  <i
                    className={
                      item.likedByUser === true
                        ? "fa fa-heart"
                        : "fa fa-heart-o"
                    }
                    style={{
                      color: item.likedByUser === true ? "red" : "black",
                      cursor: "pointer",
                    }}
                  ></i>
                  <span>{item.likeCount || 0}</span>
                </div>

                {userAddress !== item.owner && (
                  <div
                    className="nft__item_like"
                    style={{ marginRight: "7px", marginTop: "6px" }}
                  >
                    <button
                      onClick={() => handleFollowToggle(item.owner)}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                      {item.isFollowing ? (
                        <svg
                          height="16"
                          width="16"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 640 512"
                        >
                          <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304l91.4 0C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7L29.7 512C13.3 512 0 498.7 0 482.3zM472 200l144 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-144 0c-13.3 0-24-10.7-24-24s10.7-24 24-24z" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 640 512"
                          height="16"
                          width="16"
                        >
                          <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304l91.4 0C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7L29.7 512C13.3 512 0 498.7 0 482.3zM504 312l0-64-64 0c-13.3 0-24-10.7-24-24s10.7-24 24-24l64 0 0-64c0-13.3 10.7-24 24-24s24 10.7 24 24l0 64 64 0c13.3 0 24 10.7 24 24s-10.7 24-24 24l-64 0 0 64c0 13.3-10.7 24-24 24s-24-10.7-24-24z" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {showMakeModal && (
        <div className="bid-modal">
          <div className="bid-modal-content relative">
            <button
              onClick={(e) => setshowMakeModal(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-lg"
            >
              ✖
            </button>
            <h3>Make Your Offer</h3>
            <div className="bid-modal-inputdiv">
              <input
                type="number"
                value={bidAmount}
                min={0.1}
                onChange={(e) =>
                  handleBidChange(e, toTez(selectedNFT.currentBid))
                }
                placeholder="Enter Offer amount 1 ꜩ or 2.05 ꜩ"
              />
            </div>

            {bidError && selectedNFT.currentBid && (
              <p className="text-red-500 text-sm mt-1 mb-2 text-left">
                Offer amount must be higher than the current offer of{" "}
                {toTez(selectedNFT.currentBid)} ꜩ
              </p>
            )}
            <div className="bid-modal-btnmaindiv">
              <button
                type="btn"
                className="btn-main"
                onClick={handleMakeOffer}
                disabled={!bidAmount || bidError}
              >
                Submit
              </button>
              <button
                type="btn"
                className="btn-clear"
                onClick={(e) => {
                  setshowMakeModal(false);
                  setBidError(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showBidModal && selectedNFT?.hasAuction && (
        <div className="bid-modal">
          <div className="bid-modal-content relative">
            <button
              onClick={(e) => setShowBidModal(false)}
              className="absolute top-3 right-3 text-gray-600 hover:text-red-500 text-lg"
            >
              ✖
            </button>

            <h3>Place Your Bid</h3>

            {selectedNFT.auctionDetails && (
              <div className="auction-info mb-4">
                <p className="text-sm mb-1">
                  <strong>Auction ends:</strong>{" "}
                  {/* {formatDate(selectedNFT.auctionDetails.end_time)} */}
                  {new Date(selectedNFT.auctionDetails.end_time).toLocaleString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true, // set to false for 24h format
                    }
                  )}
                </p>
                {new Date(selectedNFT.auctionDetails.end_time) > new Date() && (
                  <div className="flex flex-col items-center bg-white px-4 py-2 rounded-xl border border-dashed border-indigo-200 shadow-inner">
                    <span className="text-xs font-semibold text-indigo-600 uppercase mb-1">
                      Time Remaining
                    </span>
                    <div className="text-xl font-mono font-bold text-indigo-800 tracking-widest">
                      <Clock deadline={selectedNFT.auctionDetails.end_time} />
                    </div>
                  </div>
                )}
                {selectedNFT.auctionDetails.highest_bid > 0 ? (
                  <p className="text-sm">
                    <strong>Last Bid:</strong>{" "}
                    {toTez(selectedNFT.auctionDetails.highest_bid)} ꜩ
                  </p>
                ) : (
                  <p className="text-sm">No bids yet</p>
                )}
              </div>
            )}

            <div className="bid-modal-inputdiv">
              <input
                type="number"
                value={bidAmount}
                min={
                  selectedNFT.auctionDetails?.highest_bid
                    ? toTez(selectedNFT.auctionDetails.highest_bid) + 1
                    : 0.1
                }
                step="0.1"
                onChange={(e) => {
                  const value = e.target.value;
                  setBidAmount(value);

                  // Validate bid is higher than current
                  const currentBid = selectedNFT.auctionDetails?.highest_bid
                    ? toTez(selectedNFT.auctionDetails.highest_bid)
                    : 0;
                  setBidError(parseFloat(value) <= currentBid);
                }}
                placeholder={
                  selectedNFT.auctionDetails?.highest_bid
                    ? `Enter bid (min ${(
                        toTez(selectedNFT.auctionDetails.highest_bid) + 0.1
                      ).toFixed(1)} ꜩ)`
                    : "Enter bid (min 0.1 ꜩ)"
                }
              />
            </div>

            {bidError && (
              <p className="text-red-500 text-sm mt-1 mb-2 text-left">
                Bid must be higher than current highest bid
              </p>
            )}

            <div className="bid-modal-btnmaindiv">
              <button
                type="btn"
                className="btn-main"
                onClick={handlePlaceBid}
                disabled={!bidAmount || bidError}
              >
                Submit
              </button>
              <button
                type="btn"
                className="btn-clear"
                onClick={(e) => setShowBidModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {loading && (
        <div className="RoadmapSpinnerDiv" id="RoadmapSpinnerDiv">
          <span className="RoadmapSpinner"></span>
        </div>
      )}
      <CustomToastContainer ref={toastRef} />
    </>
  );
};

/* ------------------------------------------------------------------------
 * Helper utilities
 * --------------------------------------------------------------------- */
const detectCategory = (item) => {
  const mt = item.blockchain_metadata?.mime_type || item.mime_type || "";
  if (mt.includes("video")) return "Video";
  if (mt.includes("gif")) return "GIF";
  if (mt.includes("audio")) return "Audio";
  return "Image";
};

const ipfsToHttp = (url) => {
  if (!url) return "";
  if (url.startsWith("ipfs://")) {
    return url.replace("ipfs://", "https://ipfs.io/ipfs/");
  }
  return url;
};

const resolveMediaUrl = (item) =>
  ipfsToHttp(item.ipfsUrl || item.value?.ipfs_url);

const truncateAddress = (addr) =>
  addr && addr.startsWith("tz")
    ? `${addr.slice(0, 6)}…${addr.slice(-4)}`
    : addr;

export default Explore;
